import {
  defaultDataIdFromObject,
  IdGetterObj,
  StoreValue,
} from '@apollo/client/cache'
import { ArtboardDetailInfoFragment, VersionFragment } from '@sketch/gql-types'

export interface ShortIdGetterObj {
  __typename?: string
  shortId?: string
  permanentArtboardShortId?: string
  documentVersionShortId?: string
  [key: string]: StoreValue
}

export interface IdentifierGetterObj {
  __typename?: string
  identifier?: string
  [key: string]: StoreValue
}

export type DataIdObject = IdGetterObj | ShortIdGetterObj | IdentifierGetterObj

function fromFields<T>(object: DataIdObject, ...fields: (keyof NoInfer<T>)[]) {
  const missingFields = fields.filter(field => !(field in object))
  if (missingFields.length) {
    if (
      process.env.REACT_APP_ENV === 'dev' ||
      process.env.NODE_ENV === 'test'
    ) {
      throw new Error(
        `Expected object of type ${
          object.__typename
        } to have fields: ${missingFields.join(
          ', '
        )}, but got: ${JSON.stringify(object)}`
      )
    }

    return sketchDefaultIdFromObject(object)
  }

  return (
    `${object.__typename}::` +
    fields.map(field => (object as any)[field]).join('::')
  )
}

function sketchDefaultIdFromObject(object: DataIdObject) {
  const anyObject = object as IdentifierGetterObj
  if (anyObject.identifier) {
    return defaultDataIdFromObject({
      __typename: anyObject.__typename,
      id: anyObject.identifier,
    })
  }
  return defaultDataIdFromObject(object as { __typename?: string })
}

export const dataIdFromObject = (object: DataIdObject): string | undefined => {
  switch (object.__typename) {
    case 'Artboard': {
      return fromFields<ArtboardDetailInfoFragment>(
        object,
        'permanentArtboardShortId',
        'documentVersionShortId'
      )
    }
    case 'Plans': {
      /*
       * "defaultDataIdFromObject" will return null if the object doesn't contain
       * by default an "id" or "_id".
       *
       * Since the "Plans" object doesn't have an ID associated with it, it won't work for "defaultDataIdFromObject",
       * that's why we are forcing one here. So we register the object in cache and access it later on.
       */

      return `${object.__typename}`
    }

    case 'Version': {
      return fromFields<VersionFragment>(object, 'shortId')
    }

    default: {
      return sketchDefaultIdFromObject(object)
    }
  }
}
